<template>
  <section id="faq" class="faq-section">
    <div class="section-header">
      <div class="header-line"></div>
      <h2 class="section-title">Frequently <span>Asked Questions</span></h2>
      <div class="header-line"></div>
    </div>
    
    <p class="section-subtitle">Find answers to common questions about our services and solutions</p>
    
    <div class="faq-container">
      <div class="faq-item" v-for="(item, index) in faqItems" :key="index" :class="{ 'active': activeIndex === index, 'animate': isInView }" :style="{ 'animation-delay': `${0.1 * index}s` }">
        <div class="faq-question" @click="toggleItem(index)">
          <div class="question-icon">
            <i class="fas fa-question-circle"></i>
          </div>
          <h3>{{ item.question }}</h3>
          <div class="toggle-icon">
            <i class="fas" :class="activeIndex === index ? 'fa-minus' : 'fa-plus'"></i>
          </div>
        </div>
        <div class="faq-answer" :class="{ 'expanded': activeIndex === index }">
          <p v-html="item.answer"></p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'FAQSection',
  data() {
    return {
      isInView: false,
      activeIndex: null,
      faqItems: [
        {
          question: 'Why Eduval?',
          answer: 'The case for Eduval should be clear:<br><br>The value of Eduval is that they represent an integrated system of tools designed to facilitate the academic management process, with built in content and criteria by academic administrators for academic administrators. Our systems have been designed by academic reviewers, college Deans, Provost, Chief Academic Officers, and Chief Information Officers.'
        },
        {
          question: 'How can we help you?',
          answer: '1 - We will help reduce your cost<br>2 - We will help you optimize your resources<br>3 - We will help you streamline your processes<br>4 - We will help you with your performance management<br>5 - We will partner with you for the success of your students and institution.'
        },
        {
          question: 'With you at every step',
          answer: 'Eduval does not only present an integrated suite of software tools and functions, but also universally accepted content, criteria, and business rules, with intuitive navigation, and familiar look and feel. Our solutions are interactive in order to guide the user to select the right option, and make the right decision. Our solutions are easily integrated with SIS, LMS, or any other system, which creates and facilitates many important functions including, course schedule, active programs, active courses, faculty data, learning outcomes, and much more.'
        },
        {
          question: 'What makes our approach unique?',
          answer: 'Our approach combines deep academic expertise with advanced technology. We understand the challenges of modern educational institutions because our team includes former academic administrators and faculty members who have firsthand experience with these challenges. This unique perspective allows us to create solutions that are not just technically sound but academically relevant and immediately applicable.'
        },
        {
          question: 'How do we implement our solutions?',
          answer: 'We follow a collaborative implementation process that ensures successful adoption and long-term sustainability. This includes:<br><br>1. Initial assessment and needs analysis<br>2. Customized solution design<br>3. Iterative development with stakeholder feedback<br>4. Comprehensive training and support<br>5. Post-implementation evaluation and refinement'
        },
        {
          question: 'What kind of support do we provide?',
          answer: 'We offer multi-tiered support to ensure your continued success:<br><br>- 24/7 technical support for critical issues<br>- Dedicated account managers for each institution<br>- Regular system updates and enhancements<br>- On-demand training and resources<br>- Periodic reviews to ensure solution effectiveness'
        }
      ]
    }
  },
  mounted() {
    window.addEventListener('scroll', this.checkView);
    // Initial check
    this.checkView();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.checkView);
  },
  methods: {
    toggleItem(index) {
      if (this.activeIndex === index) {
        this.activeIndex = null; // Collapse if already open
      } else {
        this.activeIndex = index; // Open the clicked item
      }
    },
    checkView() {
      const section = document.querySelector('.faq-section');
      if (!section) return;
      
      const position = section.getBoundingClientRect();
      
      // Check if section is in viewport
      if (position.top < window.innerHeight && position.bottom >= 0) {
        this.isInView = true;
      }
    }
  }
}
</script>

<style scoped>
.faq-section {
  padding: 8rem 2rem;
  background-color: #fcfaf2;
  background-image: 
    radial-gradient(rgba(209, 192, 110, 0.1) 2px, transparent 2px),
    radial-gradient(rgba(209, 192, 110, 0.05) 2px, transparent 2px);
  background-size: 50px 50px, 100px 100px;
  background-position: 0 0, 25px 25px;
  position: relative;
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.header-line {
  height: 2px;
  width: 80px;
  background: linear-gradient(90deg, transparent, #d1c06e, transparent);
  margin: 0 1.5rem;
}

.section-title {
  font-size: 2.5rem;
  color: #333;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-align: center;
}

.section-title span {
  color: #d1c06e;
  position: relative;
}

.section-title span::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 100%;
  height: 3px;
  background: #d1c06e;
}

.section-subtitle {
  text-align: center;
  color: #666;
  font-size: 1.2rem;
  margin-bottom: 4rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}

.faq-container {
  max-width: 900px;
  margin: 0 auto;
}

.faq-item {
  background-color: white;
  border-radius: 10px;
  margin-bottom: 1.5rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
}

.faq-item.animate {
  animation: slide-up 0.6s ease forwards;
}

@keyframes slide-up {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.faq-item.active {
  border-left: 3px solid #d1c06e;
  box-shadow: 0 8px 25px rgba(209, 192, 110, 0.15);
}

.faq-question {
  padding: 1.5rem;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.faq-question:hover {
  background-color: rgba(209, 192, 110, 0.05);
}

.question-icon {
  margin-right: 1rem;
  font-size: 1.5rem;
  color: #d1c06e;
  flex-shrink: 0;
}

.faq-question h3 {
  font-size: 1.2rem;
  color: #333;
  margin: 0;
  flex-grow: 1;
  font-weight: 600;
}

.toggle-icon {
  color: #d1c06e;
  font-size: 1rem;
  transition: all 0.3s ease;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: rgba(209, 192, 110, 0.1);
}

.faq-item.active .toggle-icon {
  transform: rotate(180deg);
  background-color: #d1c06e;
  color: white;
}

.faq-answer {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.5s ease, padding 0.5s ease;
  padding: 0 1.5rem;
  border-top: 0px solid rgba(209, 192, 110, 0.1);
}

.faq-answer.expanded {
  max-height: 1000px;
  padding: 0 1.5rem 1.5rem;
  border-top: 1px solid rgba(209, 192, 110, 0.1);
}

.faq-answer p {
  color: #666;
  line-height: 1.6;
  margin-top: 1rem;
}

@media (max-width: 768px) {
  .faq-section {
    padding: 5rem 1.5rem;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .header-line {
    width: 50px;
  }
  
  .faq-question h3 {
    font-size: 1.1rem;
  }
}
</style> 